import { useCallback, useState } from 'react'
import DirectoryConfirmDialog from './DirectoryConfirmDialog'
import {
  Pager,
  Section,
  TableScroller,
  ToolbarInput,
  ToolbarSelect,
  fetchJSON,
  useBearer,
  useDebouncedValue,
  usePagedQuery,
  useToast,
} from './_admin-utils'

interface ClassroomTeacher {
  full_name?: string;
  email?: string;
}

interface Classroom {
  id: string;
  name?: string;
  public_name?: string;
  notes?: string;
  share_code?: string;
  space_code?: string;
  created_at?: string;
  archived_at?: string | null;
  teacher?: ClassroomTeacher;
}

interface ConfirmState {
  type: 'archive' | 'unarchive' | 'delete';
  classroom: Classroom;
  busy: boolean;
}

interface ActionMenuProps {
  archived: boolean;
  onArchiveToggle: () => void;
  onDelete: () => void;
}

const ARCHIVE_FILTERS = [
  { value: 'active', label: 'Active classrooms' },
  { value: 'archived', label: 'Archived classrooms' },
]

const DEFAULT_PAGE_SIZE = 20

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleDateString()
  } catch (error) {
    return '—'
  }
}

function ActionMenu({ archived, onArchiveToggle, onDelete }: ActionMenuProps) {
  const [open, setOpen] = useState<boolean>(false)

  const close = useCallback(() => setOpen(false), [])

  return (
    <details
      className="relative"
      open={open}
      onToggle={(event) => setOpen((event.target as HTMLDetailsElement).open)}
    >
      <summary className="inline-flex cursor-pointer list-none select-none">
        <span className="inline-flex items-center rounded-full border border-cocoa/20 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell/60">
          Actions
        </span>
      </summary>
      <div className="absolute right-0 z-10 mt-2 w-44 rounded-xl border border-white/80 bg-white p-1.5 text-sm shadow-lg">
        <button
          type="button"
          className="w-full rounded-lg px-3 py-2 text-left text-cocoa hover:bg-shell/60"
          onClick={() => {
            close()
            onArchiveToggle()
          }}
        >
          {archived ? 'Unarchive' : 'Archive'}
        </button>
        <button
          type="button"
          className="w-full rounded-lg px-3 py-2 text-left text-terracotta hover:bg-terracotta/10"
          onClick={() => {
            close()
            onDelete()
          }}
        >
          Delete…
        </button>
      </div>
    </details>
  )
}

export default function ClassroomsDirectory() {
  const bearer = useBearer()
  const { show, ToastHost } = useToast()
  const [query, setQuery] = useState<string>('')
  const debouncedQuery = useDebouncedValue(query.trim(), 300)
  const [status, setStatus] = useState<string>('active')
  const [confirm, setConfirm] = useState<ConfirmState | null>(null)

  const fetcher = useCallback(
    async ({ page, pageSize, signal }: { page: number; pageSize: number; signal?: AbortSignal }): Promise<{ items: Classroom[]; total: number }> => {
      if (!bearer) throw new Error('Not authenticated')

      const params = new URLSearchParams()
      if (debouncedQuery) params.set('q', debouncedQuery)
      if (status === 'archived') params.set('archived', '1')
      params.set('page', String(page))
      params.set('pageSize', String(pageSize))

      const raw = await fetchJSON(
        `/api/v1/admin/classrooms?${params.toString()}`,
        { bearer, signal },
      ) as { data?: { items?: Classroom[]; total?: number }; items?: Classroom[]; total?: number } | null
      const data = raw?.data || raw

      return {
        items: Array.isArray(data?.items) ? data.items : [],
        total: typeof data?.total === 'number' ? data.total : 0,
      }
    },
    [bearer, debouncedQuery, status],
  )

  const { items, setItems, page, pageSize, pageCount, loading, error, load } = usePagedQuery<Classroom>(
    fetcher,
    [fetcher],
    DEFAULT_PAGE_SIZE,
  )

  const handleConfirm = async () => {
    if (!confirm) return
    const { type, classroom } = confirm
    setConfirm((prev) => (prev ? { ...prev, busy: true } : prev))

    try {
      if (type === 'archive' || type === 'unarchive') {
        const shouldArchive = type === 'archive'
        const action = shouldArchive ? 'archive' : 'restore'
        const raw = await fetchJSON(`/api/v1/admin/classrooms/${classroom.id}/${action}`, {
          bearer,
          method: 'POST',
        }) as { data?: { archived_at?: string | null }; classroom?: { archived_at?: string | null } } | null
        const result = raw?.data || raw?.classroom || raw
        const archivedAt = (result as { archived_at?: string | null })?.archived_at || (shouldArchive ? new Date().toISOString() : null)

        setItems((prev) => {
          if (shouldArchive && status === 'active') {
            return prev.filter((item) => item.id !== classroom.id)
          }
          if (!shouldArchive && status === 'archived') {
            return prev.filter((item) => item.id !== classroom.id)
          }
          return prev.map((item) =>
            item.id === classroom.id ? { ...item, archived_at: archivedAt } : item,
          )
        })

        show(shouldArchive ? 'Classroom archived' : 'Classroom restored')
      } else if (type === 'delete') {
        await fetchJSON(`/api/v1/admin/classrooms/${classroom.id}`, {
          bearer,
          method: 'DELETE',
        })

        setItems((prev) => prev.filter((item) => item.id !== classroom.id))
        show('Classroom deleted')
      }

      await load(page)
    } catch (actionError) {
      if ((actionError as Error)?.name === 'AbortError') return
      show((actionError as Error)?.message || 'Unable to complete action')
    } finally {
      setConfirm(null)
    }
  }

  const confirmTitle = confirm
    ? confirm.type === 'delete'
      ? 'Delete classroom'
      : confirm.type === 'archive'
        ? 'Archive classroom'
        : 'Unarchive classroom'
    : ''

  const confirmDescription = confirm
    ? confirm.type === 'delete'
      ? 'This permanently removes the classroom and all related data. This action cannot be undone.'
      : confirm.type === 'archive'
        ? 'Archived classrooms are hidden from active views but can be restored later.'
        : 'Unarchived classrooms will appear in active views again.'
    : ''

  return (
    <Section
      title="Classrooms Directory"
      actions={
        <a
          href="/admin/provision-teacher"
          className="rounded-full border border-cocoa/20 bg-white px-4 py-1.5 text-sm font-semibold text-cocoa hover:bg-shell/60"
        >
          Provision teacher
        </a>
      }
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
          <div className="w-full md:w-64">
            <ToolbarInput
              value={query}
              onChange={setQuery}
              placeholder="Search by name or code…"
            />
          </div>
          <div className="w-full md:w-48">
            <ToolbarSelect value={status} onChange={setStatus} options={ARCHIVE_FILTERS} />
          </div>
        </div>
        <Pager
          page={page}
          pageCount={pageCount}
          onPage={(next) => load(next, pageSize)}
        />
      </div>

      <TableScroller minWidth={880}>
        <table className="w-full table-fixed border-separate border-spacing-y-2">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-cocoa/60">
              <th className="px-3 py-2">Classroom</th>
              <th className="px-3 py-2">Teacher</th>
              <th className="px-3 py-2">Share code</th>
              <th className="px-3 py-2">Space</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const isArchived = Boolean(item.archived_at)
              const displayName = item.public_name || item.name || 'Untitled classroom'

              return (
                <tr key={item.id} className="rounded-xl bg-white text-sm shadow-sm">
                  <td className="px-3 py-3 font-medium text-cocoa">
                    <div>{displayName}</div>
                    {item.notes ? (
                      <div className="mt-1 text-xs text-cocoa/60 line-clamp-2">{item.notes}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-3 text-sm text-cocoa/80">
                    {item.teacher?.full_name ? (
                      <div className="font-medium text-cocoa">{item.teacher.full_name}</div>
                    ) : (
                      <div className="text-cocoa/50">—</div>
                    )}
                    {item.teacher?.email ? (
                      <div className="text-xs text-cocoa/60">{item.teacher.email}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-3 text-sm text-cocoa/80">
                    {item.share_code ? (
                      <code className="rounded bg-shell/60 px-2 py-1 text-xs font-semibold text-cocoa">{item.share_code}</code>
                    ) : (
                      <span className="text-cocoa/50">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-sm text-cocoa/80">{item.space_code || '—'}</td>
                  <td className="px-3 py-3 text-sm text-cocoa/80">{formatDate(item.created_at)}</td>
                  <td className="px-3 py-3 text-sm text-cocoa/80">
                    {isArchived ? (
                      <span className="rounded-full bg-terracotta/10 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-terracotta">
                        Archived
                      </span>
                    ) : (
                      <span className="rounded-full bg-mint/20 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-mint">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <ActionMenu
                      archived={isArchived}
                      onArchiveToggle={() =>
                        setConfirm({ type: isArchived ? 'unarchive' : 'archive', classroom: item, busy: false })
                      }
                      onDelete={() => setConfirm({ type: 'delete', classroom: item, busy: false })}
                    />
                  </td>
                </tr>
              )
            })}
            {!loading && items.length === 0 ? (
              <tr>
                <td className="px-3 py-6 text-center text-sm text-cocoa/60" colSpan={7}>
                  No classrooms found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </TableScroller>

      {loading ? <p className="mt-4 text-sm text-cocoa/60">Loading classrooms…</p> : null}
      {error ? (
        <p className="mt-4 rounded-lg border border-terracotta/40 bg-terracotta/10 p-3 text-sm text-terracotta">
          {error}
        </p>
      ) : null}

      <DirectoryConfirmDialog
        open={Boolean(confirm)}
        title={confirmTitle}
        description={confirmDescription}
        confirmLabel={
          confirm?.type === 'delete'
            ? 'Delete classroom'
            : confirm?.type === 'archive'
              ? 'Archive classroom'
              : 'Unarchive classroom'
        }
        confirmTone={confirm?.type === 'delete' ? 'danger' : 'default'}
        busy={Boolean(confirm?.busy)}
        onCancel={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />

      <ToastHost />
    </Section>
  )
}
